"use client";

import { useState } from "react";
import Link from "next/link";

// Everything here runs in the browser — no AI, no account, no server. It gives
// instant, deterministic signals (the kind a writer can act on immediately) and
// then points to the full rubric-based marking, which is the paid product.

const STOPWORDS = new Set(
  "the a an and or but so of to in on at for with as is are was were be been being it its this that these those i you he she they we me my your his her their our them him us do does did have has had will would can could should not no yes if then than out up down then there here what which who when where why how all any some".split(
    " ",
  ),
);

const WEAK_WORDS = [
  "very",
  "really",
  "quite",
  "just",
  "actually",
  "basically",
  "literally",
  "thing",
  "things",
  "stuff",
  "got",
  "good",
  "bad",
  "nice",
  "a lot",
  "kind of",
  "sort of",
];

const SAMPLE =
  "The storm was very bad. The waves were really big and the boat was small. We were scared. The captain said it was just a normal storm but it did not feel normal. The thing is, nobody had ever seen waves like that before, and they kept getting bigger and bigger and bigger.";

type Analysis = {
  words: number;
  sentences: number;
  avgLen: number;
  longest: number;
  lenSpread: number;
  repeats: { word: string; count: number }[];
  weak: { word: string; count: number }[];
  adverbs: number;
};

function analyse(text: string): Analysis | null {
  const trimmed = text.trim();
  if (!trimmed) return null;

  const sentenceParts = trimmed
    .split(/[.!?]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const wordTokens = trimmed.toLowerCase().match(/\b[a-z']+\b/g) ?? [];
  const words = wordTokens.length;
  const sentences = Math.max(sentenceParts.length, 1);

  const sentenceLens = sentenceParts.map((s) => (s.match(/\b[a-z']+\b/gi) ?? []).length);
  const longest = sentenceLens.length ? Math.max(...sentenceLens) : 0;
  const shortest = sentenceLens.length ? Math.min(...sentenceLens) : 0;

  // Repeated content words (skip stopwords + very short words).
  const counts = new Map<string, number>();
  for (const w of wordTokens) {
    if (w.length < 4 || STOPWORDS.has(w)) continue;
    counts.set(w, (counts.get(w) ?? 0) + 1);
  }
  const repeats = [...counts.entries()]
    .filter(([, c]) => c >= 3)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4)
    .map(([word, count]) => ({ word, count }));

  const lower = ` ${trimmed.toLowerCase()} `;
  const weak = WEAK_WORDS.map((w) => {
    const re = new RegExp(`\\b${w.replace(/ /g, "\\s+")}\\b`, "g");
    const count = (lower.match(re) ?? []).length;
    return { word: w, count };
  }).filter((x) => x.count > 0);

  const adverbs = wordTokens.filter((w) => w.endsWith("ly") && w.length > 4).length;

  return {
    words,
    sentences,
    avgLen: Math.round(words / sentences),
    longest,
    lenSpread: longest - shortest,
    repeats,
    weak,
    adverbs,
  };
}

// Stat: tool result UI — p-5 kept as div (not Card), token classes converted to utilities
function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-2xl border border-line bg-paper-2 p-5">
      <div className="font-display text-3xl font-semibold tabular-nums">{value}</div>
      <div className="mt-1 text-sm font-bold text-ink">{label}</div>
      {hint && <div className="mt-0.5 text-xs text-ink-soft">{hint}</div>}
    </div>
  );
}

// Finding: tool result UI — bg-[var(--gold)]/20 kept as arbitrary (no bg-gold-soft opacity variant); emerald left as-is
function Finding({ ok, children }: { ok: boolean; children: React.ReactNode }) {
  return (
    <li className="flex gap-3 leading-relaxed">
      <span
        className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full text-xs font-bold ${
          ok ? "bg-emerald-500/15 text-emerald-700" : "bg-[var(--gold)]/20 text-gold"
        }`}
      >
        {ok ? "✓" : "!"}
      </span>
      <span className="text-[15px] text-ink">{children}</span>
    </li>
  );
}

export function WritingCheck() {
  const [text, setText] = useState("");
  const [result, setResult] = useState<Analysis | null>(null);
  const [ran, setRan] = useState(false);

  const run = () => {
    setResult(analyse(text));
    setRan(true);
  };

  return (
    <div>
      {/* Textarea: tool internal UI — left as-is per instruction */}
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={8}
        placeholder="Paste a paragraph or two here…"
        className="w-full resize-y rounded-2xl border border-[var(--line)] bg-[var(--paper-2)] p-5 leading-relaxed text-[var(--ink)] transition-colors outline-none focus:border-[var(--gold)]"
      />
      {/* Tool action buttons: internal UI — left as-is */}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          onClick={run}
          disabled={!text.trim()}
          className="rounded-full bg-[var(--ink)] px-6 py-3 text-sm font-bold text-[var(--paper-2)] transition-transform hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Check my writing
        </button>
        <button
          onClick={() => {
            setText(SAMPLE);
            setResult(analyse(SAMPLE));
            setRan(true);
          }}
          className="text-sm font-bold text-[var(--ink-soft)] transition-colors hover:text-[var(--ink)]"
        >
          Try a sample
        </button>
        <span className="text-xs text-[var(--ink-soft)]">
          Runs in your browser. Your text never leaves it.
        </span>
      </div>

      {ran && !result && <p className="mt-8 text-ink-soft">Add some text and check again.</p>}

      {result && (
        <div className="mt-10">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="Words" value={String(result.words)} />
            <Stat label="Sentences" value={String(result.sentences)} />
            <Stat label="Avg sentence" value={`${result.avgLen}`} hint="words — aim ~12–20" />
            <Stat label="Longest" value={`${result.longest}`} hint="words in one sentence" />
          </div>

          <h3 className="font-display mt-10 text-xl font-semibold">What stands out</h3>
          <ul className="mt-4 space-y-3">
            <Finding ok={result.lenSpread >= 8}>
              {result.lenSpread >= 8
                ? "Good sentence-length variety. Your rhythm has movement."
                : "Your sentences are similar lengths. Mix a short, punchy one in among the longer ones for rhythm."}
            </Finding>
            <Finding ok={result.longest <= 40}>
              {result.longest <= 40
                ? "No runaway sentences. Nothing runs over 40 words."
                : `One sentence runs ${result.longest} words. Split it so the reader doesn't lose the thread.`}
            </Finding>
            <Finding ok={result.repeats.length === 0}>
              {result.repeats.length === 0
                ? "No obvious word repetition."
                : `Repeated words: ${result.repeats
                    .map((r) => `"${r.word}" ×${r.count}`)
                    .join(", ")}. Try a stronger synonym or rephrase.`}
            </Finding>
            <Finding ok={result.weak.length === 0}>
              {result.weak.length === 0
                ? "No filler words flagged. You're choosing precise words here."
                : `Weak/filler words: ${result.weak
                    .map((w) => `"${w.word}"`)
                    .join(", ")}. Swap them for something precise.`}
            </Finding>
            <Finding ok={result.adverbs <= Math.max(2, Math.round(result.words / 80))}>
              {result.adverbs <= Math.max(2, Math.round(result.words / 80))
                ? "You're not overusing adverbs."
                : `${result.adverbs} "-ly" adverbs. A stronger verb beats verb + adverb (say "sprinted," not "ran quickly").`}
            </Finding>
          </ul>

          {/*
            Marketing CTA block (chrome): dark bg-ink container with bg-red CTA.
            NOT standard Button primary (which uses bg-ink) — left as <Link>;
            token classes converted to utilities.
          */}
          <div className="mt-10 rounded-3xl border border-line bg-ink p-7 text-center text-paper-2">
            <h3 className="font-display text-2xl font-semibold">
              This checks your mechanics. Want the full rubric mark?
            </h3>
            <p className="mx-auto mt-2 max-w-md text-sm text-white/70">
              These are quick mechanical checks. Coach Pen reads your writing against a real rubric
              and tells you what to fix, then trains you on the technique to fix it.
            </p>
            <div className="mt-5">
              <Link
                href="/auth/signup"
                className="inline-block rounded-full bg-red px-7 py-3 text-sm font-bold text-paper-2 transition-transform hover:-translate-y-0.5"
              >
                Get a full mark, free
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
